"use client"

import "@/components/compare/compare-glass.css"

import type { ReactNode } from "react"
import Image from "next/image"
import Link from "next/link"
import { motion, MotionConfig } from "motion/react"
import {
  ArrowRight,
  Camera,
  GitCompare,
  LayoutGrid,
  Package,
  ScanSearch,
  ShoppingBag,
  Smartphone,
  Stethoscope,
  type LucideIcon,
} from "lucide-react"
import { AnimatedBackground } from "@/components/ui/animated-background"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { Reveal, Stagger } from "@/components/motion"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { analytics, trackEvent } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { LIST_IMPRESSION_THRESHOLD } from "@/components/analytics/content-module-impression"
import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import {
  getAllComparisons,
  type ComparisonEntry,
  type GrassComparisonEntry,
  type BrandComparisonEntry,
  type TopicComparisonEntry,
  type TopicGroup,
  type GrassKey,
} from "@/data/comparisons"
import { getGrassReferencePhotoMeta } from "@/data/grass-reference-photos"
import { getCompareGrassProductKey, getProductByKey } from "@/data/guide-product-recommendations"
import { ComparePhotoCredits, referencePhotoTitle } from "@/components/compare/compare-versus-panel"
import { AppBrandTile } from "@/components/compare/app-brand-tile"

// Per-side season → tint variant for the glass VS-split card. Mirrors the
// source used by components/compare/comparison-icon.tsx so the landing cards
// stay in sync with the rest of the compare surface.
function seasonKind(key: GrassKey): "warm" | "cool" {
  return GRASS_CHARACTERISTICS[key]?.season === "Warm" ? "warm" : "cool"
}

const COMPARE_CARD_CTA_LOCATION = "compare_index_card"

interface CompareCardProps {
  comparison: ComparisonEntry
  /** Section the card sits in; pairs with the section impression. */
  listId: string
  /** 1-based position within the section. */
  position: number
}

/**
 * One impression per card section, not per card: the index renders dozens of
 * cards, and per-card CTR comes from `position` on the click.
 */
function CompareCardList({
  listId,
  count,
  children,
}: {
  listId: string
  count: number
  children: ReactNode
}) {
  const ref = useImpressionEvent<HTMLDivElement>(
    COMPARE_CARD_CTA_LOCATION,
    { surface: "compare_index", list_id: listId, count },
    { emit: analytics.ctaSurfaceShownWhenVisible, threshold: LIST_IMPRESSION_THRESHOLD },
  )
  return <div ref={ref}>{children}</div>
}

// Grass side: reference photo thumbnail + name + season chip, tinted cool
// (blue) or warm (amber). Photo credits live in the footer credit line.
function GrassVsSide({ side }: { side: GrassComparisonEntry["a"] }) {
  const kind = seasonKind(side.key)
  const photo = getGrassReferencePhotoMeta(side.key)
  // Grasses without a verified reference photo (annual rye, the cool-season
  // blend) fall back to their seed packshot so the VS card stays balanced.
  const seedKey = photo ? null : getCompareGrassProductKey(side.guideSlug)
  const seedImage = seedKey ? getProductByKey(seedKey)?.imageUrl : undefined
  return (
    <div className={`cmp-vs__side cmp-vs__side--${kind}`}>
      {photo ? (
        <div className="cmp-vs__thumb">
          <Image
            src={photo.src}
            alt={`${side.displayName} close-up`}
            title={referencePhotoTitle(photo)}
            fill
            unoptimized
            sizes="(max-width: 768px) 45vw, 260px"
          />
        </div>
      ) : seedImage ? (
        <div className="cmp-vs__thumb cmp-vs__thumb--product">
          <Image
            src={seedImage}
            alt={`${side.displayName} seed product packaging`}
            fill
            sizes="(max-width: 768px) 45vw, 260px"
          />
        </div>
      ) : null}
      <span className="cmp-vs__name">{side.displayName}</span>
      <span className="cmp-vs__tag">
        {kind === "warm" ? "Warm-season" : "Cool-season"}
      </span>
    </div>
  )
}

// Brand side: product packshot (when the side names an Amazon product) + brand
// name + neutral "Option A / B" chip.
function BrandVsSide({
  side,
  optionLabel,
}: {
  side: BrandComparisonEntry["a"]
  optionLabel: string
}) {
  const product = side.productKey ? getProductByKey(side.productKey) : undefined
  return (
    <div className={`cmp-vs__side${side.appTile ? " cmp-vs__side--app" : ""}`}>
      {side.appTile ? (
        // App matchups: our real app icon on our side, a typeset wordmark plate
        // on the competitor's. Neither side has a packshot to fall back on.
        <div className="cmp-vs__thumb">
          <AppBrandTile tile={side.appTile} name={side.displayName} variant="thumb" />
        </div>
      ) : product?.imageUrl ? (
        <div className="cmp-vs__thumb cmp-vs__thumb--product">
          <Image
            src={product.imageUrl}
            // Name the pictured product, not the side label — they differ on
            // sides whose image is a recommended product (e.g. Sunday-vs-DIY).
            alt={`${product.name} product packaging`}
            fill
            sizes="(max-width: 768px) 45vw, 260px"
          />
        </div>
      ) : (
        // Sides with no Amazon SKU (e.g. Sunday, a mail-order subscription) get a
        // neutral icon tile so the VS split stays visually balanced.
        <div className="cmp-vs__thumb cmp-vs__thumb--icon" aria-hidden="true">
          <Package className="cmp-vs__thumb-ic" />
        </div>
      )}
      <span className="cmp-vs__name">{side.brand}</span>
      <span className="cmp-vs__tag">{optionLabel}</span>
    </div>
  )
}

// Landing sections for the topic comparisons. `diagnostic` and `landscape` hold
// one page each, so they share a section rather than each leaving half of a
// two-column row empty. `groups` stays a list so a future third entry in either
// group needs no code change here.
const TOPIC_SECTIONS: {
  id: string
  groups: TopicGroup[]
  heading: string
  Icon: LucideIcon
  tint: string
  ink: string
}[] = [
  {
    id: "yard-calls",
    groups: ["diagnostic", "landscape"],
    heading: "Lawn Diagnosis & Yard Decisions",
    Icon: Stethoscope,
    tint: "bg-rose-100",
    ink: "text-rose-700",
  },
  {
    id: "lookalike",
    groups: ["lookalike"],
    heading: "Weed or Grass? Lookalike ID",
    Icon: ScanSearch,
    tint: "bg-violet-100",
    ink: "text-violet-700",
  },
]

// Per-card icon fallback, used when a topic side has no sourced photo. Keyed off
// the card's own group rather than its landing section, so a merged section
// still gives each card the glyph that matches what it is about.
const TOPIC_GROUP_ICONS: Record<TopicGroup, LucideIcon> = {
  diagnostic: Stethoscope,
  lookalike: ScanSearch,
  landscape: LayoutGrid,
}

// Topic side: its own attributed photo when one is sourced, otherwise the
// topic-group icon tile so the VS split stays balanced (same fallback pattern
// the brand cards use for sides with no packshot).
function TopicVsSide({
  side,
  Icon,
}: {
  side: TopicComparisonEntry["a"]
  Icon: LucideIcon
}) {
  return (
    <div className="cmp-vs__side">
      {side.photo ? (
        <div className="cmp-vs__thumb">
          <Image
            src={side.photo.src}
            alt={side.photo.caption ?? `What ${side.displayName} looks like`}
            title={referencePhotoTitle(side.photo)}
            fill
            unoptimized
            sizes="(max-width: 768px) 45vw, 260px"
          />
        </div>
      ) : (
        <div className="cmp-vs__thumb cmp-vs__thumb--icon" aria-hidden="true">
          <Icon className="cmp-vs__thumb-ic" />
        </div>
      )}
      <span className="cmp-vs__name">{side.displayName}</span>
      <span className="cmp-vs__tag">{side.sub}</span>
    </div>
  )
}

function topicIconFor(group: TopicGroup) {
  return TOPIC_GROUP_ICONS[group] ?? ScanSearch
}

function CompareCard({ comparison: c, listId, position }: CompareCardProps) {
  return (
    <Link
      href={`/compare/${c.slug}`}
      onClick={() =>
        trackEvent("compare_card_clicked", {
          slug: c.slug,
          kind: c.kind,
          cta_location: COMPARE_CARD_CTA_LOCATION,
          list_id: listId,
          position,
        })
      }
      className="cmp-card group h-full"
    >
      {c.kind === "grass" ? (
        <div className="cmp-vs">
          <GrassVsSide side={c.a} />
          <span className="cmp-vs__medal" aria-hidden="true">
            VS
          </span>
          <GrassVsSide side={c.b} />
        </div>
      ) : c.kind === "topic" ? (
        <div className="cmp-vs cmp-vs--brand">
          <TopicVsSide side={c.a} Icon={topicIconFor(c.topicGroup)} />
          <span className="cmp-vs__medal" aria-hidden="true">
            VS
          </span>
          <TopicVsSide side={c.b} Icon={topicIconFor(c.topicGroup)} />
        </div>
      ) : (
        <div className="cmp-vs cmp-vs--brand">
          <BrandVsSide side={c.a} optionLabel="Option A" />
          <span className="cmp-vs__medal" aria-hidden="true">
            VS
          </span>
          <BrandVsSide side={c.b} optionLabel="Option B" />
        </div>
      )}
      <div className="cmp-card__body">
        <p className="cmp-card__desc">{c.metaDescription}</p>
        <span className="cmp-card__go">
          Read the comparison
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </div>
    </Link>
  )
}

export function CompareLandingContent() {
  const comparisons = getAllComparisons()
  const grassComparisons = comparisons.filter(
    (c): c is GrassComparisonEntry => c.kind === "grass",
  )
  // Brand entries split by `category`, which defaults to 'product' when unset,
  // so every pre-existing brand entry keeps landing in the product section.
  const brandComparisons = comparisons.filter(
    (c): c is BrandComparisonEntry => c.kind === "brand",
  )
  const productComparisons = brandComparisons.filter((c) => c.category !== "app")
  const appComparisons = brandComparisons.filter((c) => c.category === "app")
  const topicComparisons = comparisons.filter(
    (c): c is TopicComparisonEntry => c.kind === "topic",
  )

  return (
    <MotionConfig reducedMotion="user">
      <div className="cmp-root min-h-screen relative">
        <AnimatedBackground />
        <div className="relative">
          <div className="max-w-6xl mx-auto px-4 pt-6 md:pt-8 md:px-6 lg:px-8 pb-12 md:pb-16">
            <div className="mb-2">
              {/* Decorative only: page.tsx emits the BreadcrumbList JSON-LD. */}
              <BreadcrumbComponent
                emitSchema={false}
                items={[
                  { label: "Home", href: "/" },
                  { label: "Compare" },
                ]}
              />
            </div>

            {/* Hero */}
            <motion.div
              {...FADE_UP}
              transition={FADE_TRANSITION}
              className="py-3 md:py-5"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                Decision guides
              </p>
              <h1 className="mt-1 text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
                Grass &amp; Lawn Comparisons
              </h1>
              <p className="mt-2 max-w-2xl text-base text-gray-600 leading-relaxed sm:text-lg">
                Hand-curated head-to-head guides for your lawn. Compare grass
                types on climate, mowing, water, traffic, and cost, tell a weed
                apart from the grass it is imitating, work out whether a brown
                lawn is dormant or dead, and size up fertilizer, seed, turf, and
                lawn services. Every guide is written from first principles using
                verified university extension data, not regurgitated marketing
                copy.
              </p>
            </motion.div>

            {/* CTA banner */}
            <Reveal
              onMount
              className="mb-8 md:mb-12 px-4 md:px-5 py-3 rounded-2xl bg-linear-to-r from-green-600 to-emerald-600 text-white"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <p className="text-sm md:text-base font-medium">
                  <span className="font-bold">Not sure what grass you have?</span>{" "}
                  <span className="text-green-100">
                    Upload a photo and our AI identifies it in seconds.
                  </span>
                </p>
                <div className="flex flex-col xs:flex-row gap-2 w-full md:w-auto md:flex-shrink-0">
                  <Link
                    href="/"
                    className="flex-1 md:flex-none inline-flex min-h-[44px] min-w-0 items-center justify-center gap-1.5 bg-white text-green-700 px-3 py-2.5 md:px-4 rounded-lg font-semibold text-sm hover:bg-green-50 transition-colors"
                  >
                    <Camera className="w-4 h-4 shrink-0" />
                    Identify My Grass
                  </Link>
                  <Link
                    href="/tools/grass-comparison"
                    className="flex-1 md:flex-none inline-flex min-h-[44px] min-w-0 items-center justify-center gap-1.5 bg-white/20 text-white px-3 py-2.5 md:px-4 rounded-lg font-semibold text-sm hover:bg-white/30 transition-colors"
                  >
                    <GitCompare className="w-4 h-4 shrink-0" />
                    Compare Tool
                  </Link>
                </div>
              </div>
            </Reveal>

            {/* Grass type matchups */}
            {grassComparisons.length > 0 && (
              <Reveal onMount className="mb-8 md:mb-16">
                <div className="flex items-center gap-2 mb-3 md:mb-6 flex-wrap">
                  <div className="p-1.5 bg-emerald-100 rounded-lg">
                    <GitCompare className="w-5 h-5 text-emerald-700" />
                  </div>
                  <h2 className="text-lg md:text-3xl font-bold text-gray-900">
                    Grass Type Matchups
                  </h2>
                </div>
                <CompareCardList listId="grass" count={grassComparisons.length}>
                  <Stagger
                    onMount
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
                  >
                    {grassComparisons.map((c, i) => (
                      <Stagger.Item key={c.slug}>
                        <CompareCard comparison={c} listId="grass" position={i + 1} />
                      </Stagger.Item>
                    ))}
                  </Stagger>
                </CompareCardList>
              </Reveal>
            )}

            {/* Diagnosis, weed-vs-grass ID, and whole-yard surface decisions */}
            {TOPIC_SECTIONS.map((section) => {
              const items = topicComparisons.filter((c) =>
                section.groups.includes(c.topicGroup),
              )
              if (items.length === 0) return null
              const { Icon } = section
              return (
                <Reveal key={section.id} onMount className="mb-8 md:mb-16">
                  <div className="flex items-center gap-2 mb-3 md:mb-6 flex-wrap">
                    <div className={`p-1.5 ${section.tint} rounded-lg`}>
                      <Icon className={`w-5 h-5 ${section.ink}`} />
                    </div>
                    <h2 className="text-lg md:text-3xl font-bold text-gray-900">
                      {section.heading}
                    </h2>
                  </div>
                  <CompareCardList listId={section.id} count={items.length}>
                    <Stagger
                      onMount
                      className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
                    >
                      {items.map((c, i) => (
                        <Stagger.Item key={c.slug}>
                          <CompareCard comparison={c} listId={section.id} position={i + 1} />
                        </Stagger.Item>
                      ))}
                    </Stagger>
                  </CompareCardList>
                </Reveal>
              )
            })}

            {/* Lawn app comparisons. Separate from the product section because
                a reader choosing between two apps is not shopping for a bag,
                and one side of every card here is our own product. */}
            {appComparisons.length > 0 && (
              <Reveal onMount className="mb-8 md:mb-16">
                <div className="flex items-center gap-2 mb-3 md:mb-6 flex-wrap">
                  <div className="p-1.5 bg-indigo-100 rounded-lg">
                    <Smartphone className="w-5 h-5 text-indigo-700" />
                  </div>
                  <h2 className="text-lg md:text-3xl font-bold text-gray-900">
                    Lawn App Comparisons
                  </h2>
                </div>
                <CompareCardList listId="app" count={appComparisons.length}>
                  <Stagger
                    onMount
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
                  >
                    {appComparisons.map((c, i) => (
                      <Stagger.Item key={c.slug}>
                        <CompareCard comparison={c} listId="app" position={i + 1} />
                      </Stagger.Item>
                    ))}
                  </Stagger>
                </CompareCardList>
                <div className="mt-4">
                  <Link
                    href="/best-grass-identification-app"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-700 hover:text-indigo-800 underline underline-offset-4 decoration-2"
                  >
                    See every grass identification app side by side
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </Reveal>
            )}

            {/* Brand comparisons */}
            {productComparisons.length > 0 && (
              <Reveal onMount className="mb-8 md:mb-16">
                <div className="flex items-center gap-2 mb-3 md:mb-6 flex-wrap">
                  <div className="p-1.5 bg-slate-100 rounded-lg">
                    <ShoppingBag className="w-5 h-5 text-slate-700" />
                  </div>
                  <h2 className="text-lg md:text-3xl font-bold text-gray-900">
                    Product & Brand Comparisons
                  </h2>
                  <span className="text-xs md:text-sm text-gray-500 ml-1">
                    Picking fertilizer, seed, and feed without the marketing fog
                  </span>
                </div>
                <CompareCardList listId="product" count={productComparisons.length}>
                  <Stagger
                    onMount
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
                  >
                    {productComparisons.map((c, i) => (
                      <Stagger.Item key={c.slug}>
                        <CompareCard comparison={c} listId="product" position={i + 1} />
                      </Stagger.Item>
                    ))}
                  </Stagger>
                </CompareCardList>
              </Reveal>
            )}
          </div>
        </div>
      </div>
      {/* Photo credits: last in the tree, moved below the site footer on mount. */}
      <ComparePhotoCredits
        photos={[
          ...grassComparisons.flatMap((c) => [
            { name: c.a.displayName, photo: getGrassReferencePhotoMeta(c.a.key) },
            { name: c.b.displayName, photo: getGrassReferencePhotoMeta(c.b.key) },
          ]),
          ...topicComparisons.flatMap((c) => [
            { name: c.a.displayName, photo: c.a.photo ?? null },
            { name: c.b.displayName, photo: c.b.photo ?? null },
          ]),
        ]}
      />
    </MotionConfig>
  )
}
